// ─── src/components/elevateFeedback/analysis.ts ──────────────────────────────
//
// Arabic labels (as participants saw them on the Elevate Damascus 2026
// feedback form), aggregate stats, auto-generated highlights and the CSV
// export for the staff "Elevate Feedback" view.

import type {
  ElevateDayKey,
  ElevateFeedback,
  ElevatePillarKey,
} from "../../api/elevate";
import type { RatingStats } from "../../api/gewFeedback";

export const DAY_LABEL: Record<ElevateDayKey, string> = {
  day1: "اليوم الأول · تمويل الشركات الناشئة والجاهزية للاستثمار",
  day2: "اليوم الثاني · رأس المال المغامر، وشروط الصفقات، والعناية الواجبة",
  day3: "اليوم الثالث · ما بعد الاستثمار، وغرفة العروض، ومحاكاة الصفقات",
};
export const DAY_SHORT: Record<ElevateDayKey, string> = {
  day1: "اليوم الأول",
  day2: "اليوم الثاني",
  day3: "اليوم الثالث",
};
export const DAY_KEYS: ElevateDayKey[] = ["day1", "day2", "day3"];

export const SESSION_LABEL: Record<string, string> = {
  d1_funding_guide: "دليل تمويل الشركات الناشئة",
  d1_million_deal: "من داخل صفقة شركة ناشئة بقيمة مليون دولار",
  d1_first_check: "الشيك الأول: كيف يفكّر المستثمر الملاك",
  d1_after_pitch: "ما بعد العرض: ما تعلّمه المؤسسون عن جمع التمويل",
  d2_vc_engine: "من داخل محرّك رأس المال المغامر",
  d2_term_sheets: "فكّ شيفرة شروط الصفقات",
  d2_due_diligence: "أساسيات العناية الواجبة للشركات الناشئة",
  d2_beyond_borders: "ما وراء الحدود: دروس من منظومات ريادة الأعمال العالمية",
  d2_ask_investors: "اسأل المستثمرين: ما الذي يتطلّبه دعم شركة ناشئة؟",
  d2_red_flags: "إشارات الخطر وإشارات النمو: تقييم الأعمال",
  d3_after_check: "ما بعد الشيك: خلق القيمة وإدارة المحافظ والتخارج",
  d3_seed_to_scale: "من البذرة إلى التوسّع: قصص نمو حقيقية",
  d3_pitch_room: "داخل غرفة العروض: عروض خاصة للشركات الناشئة",
  d3_deal_room: "غرفة الصفقات: محاكاة اتفاقيات الاستثمار",
};
export const SESSION_KEYS = Object.keys(SESSION_LABEL);
export const sessionDay = (key: string): ElevateDayKey | null =>
  key.startsWith("d1_") ? "day1" : key.startsWith("d2_") ? "day2" : key.startsWith("d3_") ? "day3" : null;

export const PILLAR_LABEL: Record<ElevatePillarKey, string> = {
  investing: "الاستثمار في الشركات الناشئة وآلياته",
  ecosystems: "فهم منظومات الشركات الناشئة وما يحفّز نموها",
  network: "بناء الثقة وتوسيع شبكة العلاقات",
  cases: "التعلّم من تجارب ودراسات حالة واقعية",
};
export const PILLAR_KEYS: ElevatePillarKey[] = ["investing", "ecosystems", "network", "cases"];

export const BALANCE_LABEL: Record<string, string> = {
  too_theory: "نظري أكثر من اللازم",
  balanced: "متوازن",
  too_practical: "تطبيقي أكثر من اللازم",
};
export const COLLABORATION_LABEL: Record<string, string> = {
  agreed: "نعم، اتفقنا على خطوة لاحقة",
  planning: "نعم، وأنوي التواصل",
  no: "لا",
};
export const DURATION_LABEL: Record<string, string> = {
  short: "قصيرة",
  right: "مناسبة",
  long: "طويلة",
};
export const NEXT_STEP_LABEL: Record<string, string> = {
  invest: "الاستثمار في شركات ناشئة سورية",
  mentor: "إرشاد المؤسسين",
  launch_program: "إطلاق برنامج داعم ضمن المؤسسة",
  partner: "بناء شراكة مع جهة من البرنامج",
  join_events: "المشاركة في فعاليات ستارت أب سوريا",
  other: "أخرى",
};

// ─── Stats ───────────────────────────────────────────────────────────────────

export const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const round1 = (n: number) => Math.round(n * 10) / 10;

export const avg = (vals: (number | null | undefined)[]): number | null => {
  const v = vals.filter((x): x is number => typeof x === "number");
  return v.length ? round1(v.reduce((a, b) => a + b, 0) / v.length) : null;
};

export const ratingStats = (vals: (number | null | undefined)[]): RatingStats => {
  const v = vals.filter((x): x is number => typeof x === "number");
  const distribution = [0, 0, 0, 0, 0];
  v.forEach((x) => {
    if (x >= 1 && x <= 5) distribution[Math.round(x) - 1] += 1;
  });
  return { average: avg(v), answered: v.length, distribution };
};

/** Counts for every key in `keys` (zeros kept), plus any unexpected keys seen. */
export const countKeys = (values: (string | null | undefined)[], keys: string[]): Record<string, number> => {
  const out: Record<string, number> = Object.fromEntries(keys.map((k) => [k, 0]));
  values.forEach((v) => {
    if (v) out[v] = (out[v] ?? 0) + 1;
  });
  return out;
};

export interface NpsStats {
  score: number | null;
  average: number | null;
  promoters: number;
  passives: number;
  detractors: number;
  /** Counts for 0..10. */
  distribution: number[];
}

export const npsStats = (rows: ElevateFeedback[]): NpsStats => {
  const distribution = Array.from({ length: 11 }, () => 0);
  let promoters = 0;
  let passives = 0;
  let detractors = 0;
  rows.forEach((r) => {
    if (typeof r.nps !== "number") return;
    distribution[Math.max(0, Math.min(10, Math.round(r.nps)))] += 1;
    if (r.nps >= 9) promoters += 1;
    else if (r.nps >= 7) passives += 1;
    else detractors += 1;
  });
  const total = promoters + passives + detractors;
  return {
    score: total ? Math.round(((promoters - detractors) / total) * 100) : null,
    average: avg(rows.map((r) => r.nps)),
    promoters,
    passives,
    detractors,
    distribution,
  };
};

export interface ElevateSummary {
  responses: number;
  overall: RatingStats;
  speakers: RatingStats;
  networking: RatingStats;
  organization: RatingStats;
  nps: NpsStats;
  days: { key: ElevateDayKey; average: number | null; attended: number }[];
  sessions: { key: string; count: number }[];
  pillars: { key: ElevatePillarKey; average: number | null }[];
  balance: Record<string, number>;
  duration: Record<string, number>;
  collaboration: Record<string, number>;
  collaborationAnswered: number;
  collaborators: number;
  nextSteps: Record<string, number>;
  quotes: number;
}

export const summarise = (rows: ElevateFeedback[]): ElevateSummary => {
  const collaboration = countKeys(rows.map((r) => r.collaboration), Object.keys(COLLABORATION_LABEL));
  return {
    responses: rows.length,
    overall: ratingStats(rows.map((r) => r.ratingOverall)),
    speakers: ratingStats(rows.map((r) => r.ratingSpeakers)),
    networking: ratingStats(rows.map((r) => r.ratingNetworking)),
    organization: ratingStats(rows.map((r) => r.ratingOrganization)),
    nps: npsStats(rows),
    days: DAY_KEYS.map((key) => {
      const v = rows.map((r) => r.dayRatings?.[key]).filter((x): x is number => typeof x === "number");
      return { key, average: avg(v), attended: v.length };
    }),
    sessions: Object.entries(countKeys(rows.flatMap((r) => r.topSessions ?? []), SESSION_KEYS))
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count || SESSION_KEYS.indexOf(a.key) - SESSION_KEYS.indexOf(b.key)),
    pillars: PILLAR_KEYS.map((key) => ({ key, average: avg(rows.map((r) => r.pillarRatings?.[key])) })),
    balance: countKeys(rows.map((r) => r.practicalBalance), Object.keys(BALANCE_LABEL)),
    duration: countKeys(rows.map((r) => r.durationFit), Object.keys(DURATION_LABEL)),
    collaboration,
    collaborationAnswered: rows.filter((r) => r.collaboration).length,
    collaborators: (collaboration.agreed ?? 0) + (collaboration.planning ?? 0),
    nextSteps: countKeys(rows.flatMap((r) => r.nextSteps ?? []), Object.keys(NEXT_STEP_LABEL)),
    quotes: rows.filter((r) => r.quoteConsent && r.quote).length,
  };
};

// ─── Highlights ──────────────────────────────────────────────────────────────

export interface Highlight {
  /** English lead-in. */
  text: string;
  /** Optional Arabic label shown after the lead-in, right-to-left. */
  ar?: string;
  tone: "good" | "warn" | "neutral";
}

/** Short, plain-language findings computed from the answers. */
export const highlights = (s: ElevateSummary): Highlight[] => {
  if (!s.responses) return [];
  const out: Highlight[] = [];
  const n = s.responses;

  const top = s.sessions[0];
  if (top && top.count > 0) {
    const tied = s.sessions.filter((x) => x.count === top.count).length;
    out.push({
      text: `Most useful session${tied > 1 ? ` (tied with ${tied - 1} other${tied > 2 ? "s" : ""})` : ""}: picked by ${top.count} of ${n} (${pct(top.count, n)}%)`,
      ar: SESSION_LABEL[top.key] ?? top.key,
      tone: "good",
    });
  }
  const unpicked = s.sessions.filter((x) => x.count === 0).length;
  if (unpicked > 0) {
    out.push({ text: `${unpicked} of ${s.sessions.length} sessions were never picked as most useful`, tone: "neutral" });
  }

  const rated = s.days.filter((d) => d.average != null);
  if (rated.length > 1) {
    const low = [...rated].sort((a, b) => (a.average ?? 0) - (b.average ?? 0))[0];
    const high = [...rated].sort((a, b) => (b.average ?? 0) - (a.average ?? 0))[0];
    if (low.average !== high.average) {
      out.push({ text: `Lowest-rated day: ${low.average} / 5 (best ${high.average})`, ar: DAY_LABEL[low.key], tone: "warn" });
    } else {
      out.push({ text: `All days rated the same: ${low.average} / 5`, tone: "neutral" });
    }
  }
  const leastAttended = [...s.days].sort((a, b) => a.attended - b.attended)[0];
  if (leastAttended && leastAttended.attended < n) {
    out.push({
      text: `${n - leastAttended.attended} respondent${n - leastAttended.attended !== 1 ? "s" : ""} skipped the least-attended day`,
      ar: DAY_SHORT[leastAttended.key],
      tone: "neutral",
    });
  }

  const pillars = s.pillars.filter((p) => p.average != null).sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
  if (pillars.length > 1 && pillars[0].average !== pillars[pillars.length - 1].average) {
    out.push({ text: `Strongest pillar: ${pillars[0].average} / 5`, ar: PILLAR_LABEL[pillars[0].key], tone: "good" });
    const w = pillars[pillars.length - 1];
    out.push({ text: `Weakest pillar: ${w.average} / 5`, ar: PILLAR_LABEL[w.key], tone: "warn" });
  }

  out.push({
    text: `${pct(s.balance.balanced ?? 0, n)}% found the theory/practice mix balanced` +
      ((s.balance.too_theory ?? 0) > 0 ? `; ${pct(s.balance.too_theory, n)}% said too theoretical` : ""),
    tone: pct(s.balance.balanced ?? 0, n) >= 60 ? "good" : "warn",
  });
  out.push({
    text: `${pct(s.duration.right ?? 0, n)}% said the length was right` +
      ` (${s.duration.short ?? 0} too short, ${s.duration.long ?? 0} too long)`,
    tone: pct(s.duration.right ?? 0, n) >= 60 ? "good" : "warn",
  });

  out.push({
    text: `${pct(s.nextSteps.invest ?? 0, n)}% plan to invest in Syrian startups and ${pct(s.nextSteps.mentor ?? 0, n)}% plan to mentor founders`,
    tone: "good",
  });
  if (s.collaborationAnswered) {
    out.push({
      text: `${pct(s.collaborators, s.collaborationAnswered)}% found an organisation to collaborate with (${s.collaboration.agreed ?? 0} already agreed a next step)`,
      tone: "good",
    });
  }
  if (s.nps.score != null) {
    out.push({
      text: `NPS ${s.nps.score > 0 ? "+" : ""}${s.nps.score}: ${s.nps.promoters} promoter${s.nps.promoters !== 1 ? "s" : ""}, ${s.nps.detractors} detractor${s.nps.detractors !== 1 ? "s" : ""}`,
      tone: s.nps.score >= 30 ? "good" : s.nps.score >= 0 ? "neutral" : "warn",
    });
  }
  return out;
};

// ─── Text helpers ────────────────────────────────────────────────────────────

export const nextStepsText = (r: ElevateFeedback) =>
  (r.nextSteps ?? [])
    .map((k) => (k === "other" && r.nextStepOther ? `أخرى: ${r.nextStepOther}` : NEXT_STEP_LABEL[k] ?? k))
    .join("، ");

export const OPEN_FIELDS = [
  ["weakSession", "Weakest session", "الجلسة الأقل فائدة"],
  ["supportNeeds", "Support needed", "الدعم المطلوب"],
  ["futureTopics", "Future topics", "مواضيع مقترحة للمستقبل"],
  ["nextStepOther", "Other next step", "خطوة تالية أخرى"],
  ["otherNotes", "Other notes", "ملاحظات أخرى"],
] as const;
export type OpenFieldKey = (typeof OPEN_FIELDS)[number][0];

/** Every free-text answer in one string, for the search box. */
export const searchableText = (r: ElevateFeedback) =>
  [r.keyTakeaway, r.weakSession, r.supportNeeds, r.futureTopics, r.nextStepOther, r.otherNotes, r.quote, r.fullName, r.roleOrg]
    .filter(Boolean)
    .join(" \n ")
    .toLowerCase();

// ─── CSV ─────────────────────────────────────────────────────────────────────

/** CSV (UTF-8 with BOM so Excel shows Arabic) of every answer, one row per response. */
export function exportElevateFeedbackCsv(rows: ElevateFeedback[], filename: string) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const day = (v: number | null | undefined) => (v == null ? "لم يحضر" : v);
  const header = [
    "Submitted at",
    "Overall (1–5)", "NPS (0–10)", "Key takeaway",
    "Day 1 (1–5)", "Day 2 (1–5)", "Day 3 (1–5)",
    "Most useful sessions", "Weakest session",
    "Speakers (1–5)", "Theory/practice balance",
    ...PILLAR_KEYS.map((k) => `Pillar: ${PILLAR_LABEL[k]} (1–5)`),
    "Networking (1–5)", "Collaboration", "Organisation (1–5)", "Duration",
    "Next steps", "Support needed", "Future topics", "Other notes",
    "Quote consent", "Quote", "Name", "Role / organisation",
  ].map(esc).join(",");
  const body = rows.map((r) =>
    [
      r.createdAt,
      r.ratingOverall, r.nps, r.keyTakeaway,
      day(r.dayRatings?.day1), day(r.dayRatings?.day2), day(r.dayRatings?.day3),
      (r.topSessions ?? []).map((k) => SESSION_LABEL[k] ?? k).join("، "), r.weakSession,
      r.ratingSpeakers, BALANCE_LABEL[r.practicalBalance] ?? r.practicalBalance,
      ...PILLAR_KEYS.map((k) => r.pillarRatings?.[k]),
      r.ratingNetworking, r.collaboration ? COLLABORATION_LABEL[r.collaboration] ?? r.collaboration : "",
      r.ratingOrganization, DURATION_LABEL[r.durationFit] ?? r.durationFit,
      nextStepsText(r), r.supportNeeds, r.futureTopics, r.otherNotes,
      r.quoteConsent ? "Yes" : "No", r.quote, r.fullName, r.roleOrg,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
